import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";

import "../DamagesVideoAnalysis.scss";
import LoadingSpinner from "../../../../components/loading-spinner/LoadingSpinner";
import Svg from "../../../../components/svg/Svg";
import { PubSubEvents } from "../../../../services/PubSubEvents";
import { RunVideoAnalysis } from "../../../../services/authorization/Permissions";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import { DamagesStorage } from "../../../../services/damage/DamagesStorage";
import { CurrentInspectionStorage } from "../../../../services/inspection/CurrentInspectionStorage";
import { VideoAnalysisService } from "../../../../services/inspection/VideoAnalysisService";
import { SubscriptionPlanUsageStorage } from "../../../../services/organization-subscription-plans/SubscriptionPlanUsageStorage";
import { translationService } from "../../../../services/translation/TranslationService";
import useTheme from "../../../../services/theme-service/ThemeService";
import "./VideoAnalysisDiagram.scss";
import OverviewPlot from "./OverviewPlot";
import CharacterizationsPlot from "./CharacterizationsPlot";
import { Info, Warning } from "../../../../components/svg/svgImageList";
import { videoPlayerService } from "../../../../services/video-player/VideoPlayerService";

interface VideoAnalysisDiagramProps {
  videoAnalysis: VideoAnalysisService;
  currentInspectionStorage: CurrentInspectionStorage;
  subscriptionPlanUsageStorage: SubscriptionPlanUsageStorage;
  damagesStorage: DamagesStorage;
}

export default observer(function DamagesVideoAnalysis(props: VideoAnalysisDiagramProps) {
  const { videoAnalysis, currentInspectionStorage, subscriptionPlanUsageStorage, damagesStorage } =
    props;
  const { themeClass } = useTheme();

  const videoAnalysisNotYetStarted = useMemo(() => {
    return (
      !videoAnalysis.isLoading &&
      videoAnalysis?.analysisNotYetStarted &&
      !videoAnalysis?.analysisInProgress
    );
  }, [
    videoAnalysis?.analysisNotYetStarted,
    videoAnalysis?.analysisInProgress,
    videoAnalysis.isLoading,
  ]);

  useEffect(() => {
    var analysisSubscribeToken = PubSub.subscribe(
      PubSubEvents.VideoAnalysisFinished,
      async (_event: string, notification: Notification) => {
        if (currentInspectionStorage.currentInspection.video?.id === notification.data.fileId) {
          await currentInspectionStorage.reloadCurrentInspection();
          await subscriptionPlanUsageStorage.getOrganizationVideoAnalysisCallsUsage();
          await videoAnalysis?.reload();
        }
      }
    );

    return function cleanup() {
      PubSub.unsubscribe(analysisSubscribeToken);
    };
  }, [videoAnalysis, currentInspectionStorage.reloadCurrentInspection, subscriptionPlanUsageStorage, currentInspectionStorage]);

  useEffect(() => {
    var segmentationSubscribeToken = PubSub.subscribe(
      PubSubEvents.VideoSegmentationFinished,
      async (_event: string, notification: Notification) => {
        if (currentInspectionStorage.currentInspection.video?.id === notification.data.fileId) {
          await currentInspectionStorage.reloadCurrentInspection();
          await subscriptionPlanUsageStorage.getOrganizationVideoAnalysisCallsUsage();
          await videoAnalysis?.reload();
        }
      }
    );

    return function cleanup() {
      PubSub.unsubscribe(segmentationSubscribeToken);
    };
  }, [videoAnalysis, currentInspectionStorage.reloadCurrentInspection, subscriptionPlanUsageStorage, currentInspectionStorage]);

  return (
    <>
      <div
        className={themeClass(
          ["video-analysis-diagram"],
          [videoAnalysisNotYetStarted ? "not-started" : ""]
        )}
      >
        {videoAnalysis.isLoading && <LoadingSpinner />}
        {currentInspectionStorage.currentInspection.video?.isConverting &&
          videoAnalysis?.analysisInProgress && (
            <div className={themeClass(["video-not-available"])}>
              <LoadingSpinner spinnerOnly />
              <p className={themeClass(["message"])}>
                {translationService.translate(
                  "damages.DamagesVideoAnalysis.video-analysis-format-message"
                )}
              </p>
              <p className={themeClass(["message"])}>
                {translationService.translate(
                  "damages.DamagesVideoAnalysis.video-analysis-format-message-automatic-start"
                )}
              </p>
            </div>
          )}
        {currentInspectionStorage.currentInspection.video?.conversionFailed &&
          videoAnalysis?.analysisInProgress && (
            <>
              <div className={themeClass(["video-failed-conversion"])}>
                <div className="svg-warning">
                  <Svg svg={Warning} width={100} />
                </div>
                {permissionsService.userHasPermissionTo(RunVideoAnalysis) ? (
                  <>
                    <p className={themeClass(["message"])}>
                      {translationService.translate(
                        "damages.DamagesVideoAnalysis.video-analysis-failed-conversion-admin-user-message"
                      )}
                    </p>
                    <p className={themeClass(["message"])}>
                      {translationService.translate(
                        "damages.DamagesVideoAnalysis.video-analysis-failed-conversion-retry"
                      )}
                    </p>
                    <p className={themeClass(["message"])}>
                      {` ${translationService.translate(
                        "damages.DamagesVideo.video-conversion-error-warning-contact"
                      )} `}
                    </p>
                  </>
                ) : (
                  <p className={themeClass(["message"])}>
                    {translationService.translate(
                      "damages.DamagesVideoAnalysis.video-analysis-failed-conversion-guest-message"
                    )}
                  </p>
                )}
              </div>
            </>
          )}
        {videoAnalysisNotYetStarted && (
          <div className="message-info">
            <Svg className="svg-info" svg={Info} />
            <span>
              {translationService.translate(
                "damages.DamagesVideoAnalysis.video-analysis-not-started"
              )}
            </span>
          </div>
        )}
        {videoAnalysis?.analysisInProgress &&
          currentInspectionStorage.currentInspection.video?.isAvailable && (
            <>
              <LoadingSpinner />
              <p>
                {translationService.translate(
                  "damages.DamagesVideoAnalysis.video-analysis-in-progress"
                )}
              </p>
            </>
          )}
        {!videoAnalysis.isLoading &&
          !videoAnalysis.analysisInProgress &&
          videoAnalysis?.analysisIsAvailable && (
            <div className="va-diagram-container">
              <div className="va-graph-container">
                <div className="va-title">
                  {translationService.translate("PlanUsage.video-analysis")}
                </div>
                {!videoPlayerService.videoDuration ? (
                  <LoadingSpinner />
                ) : (
                  <div className="va-plot-container">
                    {videoAnalysis.isOverviewDisplayed ? (
                      <OverviewPlot
                        videoAnalysisService={videoAnalysis}
                        damagesStorage={damagesStorage}
                      />
                    ) : (
                      <CharacterizationsPlot
                        videoAnalysisService={videoAnalysis}
                        damagesStorage={damagesStorage}
                      />
                    )}
                  </div>
                )}
                <div className="damage-labels-container">
                  <div>
                    {translationService.translate("damages.DamagesVideoAnalysis.added-damages")}
                  </div>
                  <div className="info-container">
                    <Svg className="svg-info" svg={Info} />
                    {translationService.translate(
                      "damages.DamagesVideoAnalysis.double-click-information"
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        {!videoAnalysis.isLoading &&
          !videoAnalysis.analysisInProgress &&
          videoAnalysis?.analysisFailed && (
            <div className={themeClass(["video-failed-analysis"])}>
              <div className="svg-warning">
                <Svg svg={Warning} width={100} />
              </div>
              <br />
              <p className={themeClass(["message"])}>
                {`${translationService.translate(
                  "damages.DamagesVideoAnalysis.video-analysis-failed"
                )} `}
                {permissionsService.userHasPermissionTo(RunVideoAnalysis) && (
                  <>
                    {translationService.translate(
                      "damages.DamagesVideoAnalysis.video-analysis-failed.run-again"
                    )}
                    <p className={themeClass(["message"])}>
                      {`${translationService.translate(
                        "damages.DamagesVideo.video-analysis-error-warning-contact"
                      )} `}
                    </p>
                  </>
                )}
                {!permissionsService.userHasPermissionTo(RunVideoAnalysis) &&
                  translationService.translate(
                    "damages.DamagesVideoAnalysis.video-analysis-failed-guest-user-message"
                  )}
              </p>
              <br />
              <p className={themeClass(["message", "margin"])}> {videoAnalysis.errorMessage}</p>
            </div>
          )}
      </div>
    </>
  );
});
